import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { GetStaticProps } from 'next';

import { isGytrStepFlow } from '@lib/page-utils/Gytr/progress';
import { sanityClient } from '@lib/sanity/client';
import type { SanityQuizPage } from '@lib/sanity/types';
import QuizBlockRenderer from '@components/quiz/QuizBlockRenderer';
import { fireViewContent } from '@lib/analytics/fireViewContent';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { toQuizPageDescription, toQuizPageTitle } from '@lib/seo';

export type AiTeamRatingPageProps = {
  quizPage: SanityQuizPage;
  hideSiteChrome: boolean;
};

/**
 * The AI Team Rating funnel page body, shared by every arm.
 *
 * FFH-1744 added a second arm at /ai-team-rating-v2 with higher prices, and
 * FFH-1743 a third at /ai-team-rating-v3 selling the control prices with a
 * forever discount. Next.js needs one route file per URL, but the arms must
 * render identically — pricing and the checkout coupon come from the Sanity
 * document, never from the route. If this component ever grows an arm-specific
 * branch, the arms have stopped being comparable and the test is measuring the
 * wrong thing.
 */
/**
 * The control arm's route. Every other arm canonicalises to it and stays out
 * of the index: the arms are identical content at experiment URLs, and this
 * is the URL that holds the page's search equity (83k clicks in the 90 days
 * to Aug 2026, the site's largest organic page - FFH-2443).
 */
const CONTROL_ARM_PATH = '/ai-team-rating';

// A purpose-built 1200x675 card rather than the site-wide FFHub.png square
// logo. Deliberately a static asset, not a CMS field: the creative changes
// about once a season, so swapping it is a one-file deploy (FFH-2563).
const SHARE_IMAGE_URL = `${FFH_HOSTNAME}/images/ai-team-rating-og.jpg`;

const AiTeamRatingFunnel = ({ quizPage }: AiTeamRatingPageProps) => {
  const router = useRouter();
  // FFH-606: View Content fires once when the funnel landing loads (the same
  // step flow that hides site chrome). fireViewContent is idempotent per
  // session.
  const isFunnelLanding = isGytrStepFlow(quizPage.blocks);

  useEffect(() => {
    if (isFunnelLanding) fireViewContent();
  }, [isFunnelLanding]);

  const title = toQuizPageTitle(quizPage.title);
  // Falls back to funnel copy rather than the site-wide default, so an editor
  // clearing the Sanity field cannot leave the site's largest organic page
  // describing the site instead of the funnel.
  const description = toQuizPageDescription(quizPage.metaDescription);
  const canonicalUrl = `${FFH_HOSTNAME}${CONTROL_ARM_PATH}`;
  const isControlArm = router.pathname === CONTROL_ARM_PATH;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} key="description" />
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        {!isControlArm && (
          // Experiment arms must not compete with the control in search.
          <meta name="robots" content="noindex" key="robots" />
        )}
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:title" content={quizPage.title} key="og:title" />
        <meta
          property="og:description"
          content={description}
          key="og:description"
        />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        <meta property="og:site_name" content={SITE_NAME} key="og:site_name" />
        {/* Keyed like the _app default, so this page-level tag wins the
            next/head merge (the rules live in lib/seo.tsx). */}
        <meta property="og:image" content={SHARE_IMAGE_URL} key="og:image" />
        {/* X falls back to og:image, but only renders the full-width card
            when twitter:card says so; the asset is 1200x675 for exactly
            that layout. */}
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        <meta
          name="twitter:image"
          content={SHARE_IMAGE_URL}
          key="twitter:image"
        />
      </Head>
      <QuizBlockRenderer quizPage={quizPage} />
    </>
  );
};

const REVALIDATE_SECONDS = 60;

/**
 * Builds `getStaticProps` for one arm of the funnel, given the GROQ query that
 * resolves its active page.
 *
 * The ISR window is what makes the Studio the control surface for both arms: an
 * editor repointing a settings singleton takes effect within ~60s with no
 * deploy, which is how the alt-pricing test is stopped.
 */
export const createAiTeamRatingStaticProps =
  (activePageQuery: string): GetStaticProps<AiTeamRatingPageProps> =>
  async () => {
    // ISR caps Sanity load at ~1 request/minute regardless of traffic. The
    // revalidation fetch bypasses the CDN so an editor switching the active
    // variant is picked up on the next revalidation (worst case ~60s) without
    // regional CDN inconsistency.
    const quizPage = await sanityClient
      .withConfig({ useCdn: false })
      .fetch<SanityQuizPage | null>(activePageQuery);

    if (!quizPage) {
      // Revalidate the 404 too, otherwise a missing document is cached until
      // the next deploy.
      return { notFound: true, revalidate: REVALIDATE_SECONDS };
    }

    return {
      props: {
        quizPage,
        hideSiteChrome: isGytrStepFlow(quizPage.blocks)
      },
      revalidate: REVALIDATE_SECONDS
    };
  };

export default AiTeamRatingFunnel;
